import { Box, Collapse, IconButton, Stack, Theme, Typography } from '@mui/material';
import { DropdownIcon, Loading, ModalContent, PlusIcon } from '@schooly/style';
import { FC, Fragment, MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { SxProps, SystemStyleObject } from '@mui/system';
import { FormattedMessage, useIntl } from 'react-intl';

import { ModalEmpty } from '../../../components/uikit-components/Modal/ModalEmpty';
import { RegisterGroupRow } from './RegisterGroupRow';
import {
  RegisterPeriod,
  RegisterPeriodGroupIds,
  RegisterPeriodStructure,
  RosterGroup,
} from './types';
import { getPeriodLabel } from './utils/registerLabels';

type PerPeriodTabContentProps = {
  structures: RegisterPeriodStructure[];
  periodsByStructureId: Record<string, RegisterPeriod[]>;
  loading: boolean;
  isEdit: boolean;
  groups: RosterGroup[];
  hasSearchQuery: boolean;
  groupIds: RegisterPeriodGroupIds;
  openStructureId: string | null;
  openPeriodId: string | null;
  onToggleStructure: (structureId: string) => void;
  onTogglePeriod: (periodId: string) => void;
  onRemoveGroupId: (id: string, periodId: string) => void;
};

export const PerPeriodTabContent: FC<PerPeriodTabContentProps> = ({
  structures,
  periodsByStructureId,
  loading,
  isEdit,
  groups,
  hasSearchQuery,
  groupIds,
  openStructureId,
  openPeriodId,
  onToggleStructure,
  onTogglePeriod,
  onRemoveGroupId,
}) => {
  const { formatMessage } = useIntl();

  if (loading) {
    return <Loading />;
  }

  if (!structures.length) {
    return (
      <ModalEmpty width="300" height="300">
        <Stack maxWidth={350}>
          <Typography align="center" variant="h3" color="common.grey2">
            <FormattedMessage id="attendance-NoPublishedTimetablesForDay" />
          </Typography>
          {!isEdit && (
            <Typography align="center" variant="h3" color="common.grey2">
              <FormattedMessage id="attendance-SelectDifferentWeekday" />
            </Typography>
          )}
        </Stack>
      </ModalEmpty>
    );
  }

  const visibleStructures = structures
    .map((structure) => ({
      structure,
      periods: (periodsByStructureId[structure.id] ?? []).filter((period) => {
        if (!hasSearchQuery) return true;

        return groups.some((group) => (groupIds[period.id] ?? []).includes(group.group_id));
      }),
    }))
    .filter(({ periods }) => periods.length > 0);

  if (hasSearchQuery && !visibleStructures.length) {
    return (
      <ModalContent active>
        <ModalEmpty>
          <Typography align="center" variant="h3">
            <FormattedMessage id="attendance-NoGroupsMatchingSearch" />
          </Typography>
        </ModalEmpty>
      </ModalContent>
    );
  }

  return (
    <ModalContent active>
      <Stack gap={1.5}>
        {visibleStructures.map(({ structure, periods }) => {
          const expanded = openStructureId === structure.id;

          return (
            <Stack
              key={structure.id}
              sx={(theme) => ({ border: theme.mixins.borderValue(), borderRadius: 1 })}
            >
              <ExpandableRow
                title={<Typography variant="h2">{structure.name}</Typography>}
                expanded={expanded}
                onToggle={() => onToggleStructure(structure.id)}
              >
                {periods.map((period, index) => {
                  const added = groups.filter((group) =>
                    (groupIds[period.id] ?? []).includes(group.group_id),
                  );
                  const periodLabel = getPeriodLabel(period, formatMessage);

                  return (
                    <Fragment key={period.id}>
                      {index > 0 && (
                        <Box sx={(theme) => ({ mx: 2, borderTop: theme.mixins.borderValue() })} />
                      )}
                      <ExpandableRow
                        title={
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="h3">{periodLabel}</Typography>
                            <Typography color="common.grey">
                              {period.start_time} – {period.end_time}
                            </Typography>
                          </Stack>
                        }
                        expanded={openPeriodId === period.id}
                        sx={{ py: 1.5 }}
                        onToggle={() => onTogglePeriod(period.id)}
                      >
                        {added.length ? (
                          <Stack px={2}>
                            {added.map((group, index) => (
                              <RegisterGroupRow
                                key={group.group_id}
                                group={group}
                                scope={{ periodId: period.id }}
                                scopeLabel={period.name}
                                onRemove={() => onRemoveGroupId(group.group_id, period.id)}
                                sx={
                                  index === added.length - 1 ? { borderBottom: 'none' } : undefined
                                }
                              />
                            ))}
                          </Stack>
                        ) : (
                          <PeriodEmptyStub />
                        )}
                      </ExpandableRow>
                    </Fragment>
                  );
                })}
              </ExpandableRow>
            </Stack>
          );
        })}
      </Stack>
    </ModalContent>
  );
};

type ExpandableRowProps = PropsWithChildren<{
  title: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  sx?: SxProps<Theme>;
}>;

const ExpandableRow: FC<ExpandableRowProps> = ({ title, expanded, onToggle, children, sx }) => (
  <Stack>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        onToggle();
      }}
      sx={(theme) => ({
        cursor: 'pointer',
        px: 2,
        py: 2,
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
    >
      {title}
      <IconButton inverse={!expanded} sx={{ transform: expanded ? 'rotate(180deg)' : undefined }}>
        <DropdownIcon />
      </IconButton>
    </Stack>
    <Collapse in={expanded}>{children}</Collapse>
  </Stack>
);

const PeriodEmptyStub: FC = () => {
  const { formatMessage } = useIntl();

  return (
    <Typography align="center" variant="h3" px={2} py={1.5}>
      <FormattedMessage id="modal-EmptyLabel-1" />
      <IconButton
        sx={{
          mx: 1,
          color: 'common.white',
          bgcolor: 'primary.main',
          '&:hover': { color: 'common.white', bgcolor: 'primary.main' },
        }}
      >
        <PlusIcon />
      </IconButton>
      <FormattedMessage
        id="modal-EmptyLabel-2"
        values={{
          label: formatMessage({ id: 'assessments-Groups' }).toLowerCase(),
        }}
      />
    </Typography>
  );
};
